/*
 @flow
 @format
*/

import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import moment from 'moment-timezone';
import 'moment/locale/es';
import type { Match } from 'react-router-dom';
import QueryString from 'query-string';
import { CSSTransition } from 'react-transition-group';

import { apiPost } from '../services/api';

import logo from '../assets/logo.png';
import userAvatar1 from '../assets/userAvatar1.jpg';
import filesIcon from '../assets/files.png';
import uploadIcon from '../assets/upload.png';
import closeIcon from '../assets/close.png';

import AlertComponent from '../components/AlertComponent';
import ErrorComponent from '../components/ErrorComponent';
import { OPENTOK_API_KEY, DISABLE_NETWORK_TEST, STATIC_FILES_URL } from '../config';

import 'react-activity/lib/Bounce/Bounce.css';

import NetworkTest, { ErrorNames } from 'opentok-network-test-js';

const OT = require('@opentok/client');

type Session = {};

const reprClientName = clientData => {
  if ('firstname' in clientData && 'lastname' in clientData) {
    return `{clientData.firstname} {clientData.lastname.charAt(0)}`;
  }
  if ('name' in clientData) {
    return clientData.name;
  }
  return 'Usuario';
};

const WaitingForSession = ({ title, subheading }) => {
  return (
    <div
      className="alert alert-info"
      style={{
        width: '70%',
        padding: '2vw',
        marginTop: '10vh',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <h4>{title}</h4>
      <h5 style={{ marginTop: 20 }}>{subheading}</h5>
      <div
        className="spinner-grow"
        style={{
          width: '10vw',
          height: '10vw',
          marginTop: '2vh',
          color: '#ccc',
        }}
        role="status"
      />
    </div>
  );
};

const ChatHeading = ({ message }) => {
  return (
    <div style={{ padding: 5, paddingBottom: 0, overflow: 'hidden' }}>
      <div
        style={{ width: 14, marginTop: 3, height: 14, float: 'left' }}
        className={`${message.source === 'mine' ? 'bg-primary' : 'bg-info'} rounded mr-2`}
      />
      <p className="mr-auto" style={{ color: '#aaa', float: 'left', marginBottom: 3 }}>
        {reprClientName(message.userDetails)}
      </p>
      <small className="text-muted" style={{ float: 'right', marginTop: 3, marginRight: 10 }}>
        {message.showTime}
      </small>
    </div>
  );
};

const ChatMessage = ({
  message,
  chatId,
  inProp,
}: {
  message: any,
  chatId: number,
  inProp: boolean,
}) => {
  console.log(`render ChatMessage ${chatId} - inprop: `, inProp);
  return (
    <CSSTransition key={chatId} in={inProp} timeout={300} classNames="fadeinanim">
      <div style={{ display: inProp ? 'block' : 'none', marginTop: 10 }}>
        <ChatHeading message={message} />
        <div
          id={`chatMsg${chatId}`}
          className="toast"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          style={{ opacity: 1, marginTop: 0, maxWidth: 'initial', marginRight: 10 }}
        >
          <div className="toast-body" style={message.sent === false ? { color: '#999' } : {}}>
            {message.eventData.msg}
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

const sizeToHuman = bytes => {
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  return `${(bytes / Math.pow(1024, i)).toFixed(2) * 1} ${sizes[i]}`;
};

const ChatAttachment = ({ message, chatId }: { message: any, chatId: number }) => {
  return (
    <>
      <ChatHeading message={message} />
      <div
        id={`chatMsg${chatId}`}
        className="toast"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{ opacity: 1, marginTop: 0, maxWidth: 'initial', marginRight: 10 }}
      >
        <div className="toast-body">
          <img
            src={filesIcon}
            style={{
              float: 'left',
              width: 'auto',
              maxHeight: '100px',
              height: '8vh',
              marginBottom: 10,
            }}
          />
          <span
            style={{
              float: 'left',
              color: '#777',
              fontSize: '0.9em',
              display: 'block',
              width: 'auto',
              padding: '0.5vh',
              marginLeft: '1vw',
              minWidth: '10%',
              maxWidth: '70%',
            }}
          >
            {message.eventData.result.name}
            <br />
            {sizeToHuman(message.eventData.result.size)}
            <br />
            <a href={STATIC_FILES_URL + message.eventData.result.fileId} target="_blank">
              descargar
            </a>
          </span>
        </div>
      </div>
    </>
  );
};

const DropZone = ({ onDrop, handleCloseUploadOverlay }) => {
  const onDropHandler = React.useCallback(acceptedFiles => {
    onDrop(acceptedFiles);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: onDropHandler });

  return (
    <div {...getRootProps()} style={{ textAlign: 'center', alignItems: 'center' }}>
      <a
        style={{ position: 'absolute', right: '50px' }}
        href=""
        onClick={ev => {
          ev.preventDefault();
          ev.stopPropagation();
          handleCloseUploadOverlay();
        }}
      >
        <img src={closeIcon} style={{ border: 0, marginRight: 10, width: 30, float: 'right' }} />
      </a>
      <input {...getInputProps()} />
      <img
        src={uploadIcon}
        style={{
          marginLeft: 'auto',
          marginRight: 'auto',
          width: 'auto',
          maxWidth: '35%',
          marginBottom: 20,
        }}
      />

      {isDragActive ? (
        <p>Arrastrar ficheros aquí.</p>
      ) : (
        <>
          <h4>Añadir ficheros.</h4>
          <span style={{ fontSize: '1.2em' }}>
            Arrastra los ficheros que desees subir aquí, o haz click para seleccionar un fichero.
          </span>
        </>
      )}
    </div>
  );
};

type Props = {
  match: Match,
};

type Attendant = {
  role: 'publisher' | 'suscriber',
};

type SessionData = {
  sessionId: string,
  scheduledDatetime: number,
  sessionDuration: number,
  attendants: Array<Attendant>,
  type: 'videocall' | 'webinar',
};

type State = {
  mainScreenMessage: null | React.Node,
  isLoading: boolean,
  hangUp: boolean,
  clients: Array<any>,
  chats: Array<any>,
  dropZoneShow: boolean,
  sessionData: SessionData | null,
  screenSharingEnabled: boolean,
};

function* getNewId() {
  let keyNum = 0;
  while (true) {
    yield keyNum;
    keyNum += 1;
  }
}

class SessionComponent extends React.Component<Props, State> {
  chatFormRef: React.Ref<'form'>;

  chatInputRef: React.Ref<'input'>;

  chatFormHistoryRef: React.Ref<'p'>;

  sessionStartTimeout: TimeoutID | null;

  otSession: {};

  constructor(props: Props) {
    super(props);
    this.state = {
      mainScreenMessage: null,
      isLoading: false,
      hangUp: false,
      lastSessionDurationDeltaSent: 0,
      clients: [],
      chats: [],
      dropZoneShow: false,
      sessionData: null,
      supportsScreenSharing: false,
      screenSharingEnabled: false,
    };
    this.handleSessionSignal = this.handleSessionSignal.bind(this);
    this.handleShowDropZone = this.handleShowDropZone.bind(this);
    this.handleFinishCall = this.handleFinishCall.bind(this);
    this.chatFormRef = React.createRef();
    this.chatInputRef = React.createRef();
    this.chatFormHistoryRef = React.createRef();
    this.sessionStartTimeout = null;
    this.sessionDurationTrackingInterval = null;
  }

  handleWaitForSession(sessionData: SessionData, proyectoData: ProyectoData) {
    this.addTimeoutToCheckSessionTimeReached(sessionData);
    const t = new Date(sessionData.scheduledDatetime);
    const fecha = moment(t).tz(proyectoData.gmt).format('LLLL');

    this.setState({
      isLoading: false,
      mainScreenMessage: (
        <WaitingForSession
          title="La sesión aún no ha empezado"
          subheading={
            <>
              <p>
                {' '}
                Está programada para el <b>{fecha}</b>{' '}
              </p>
              <p>
                {' '}
                Por favor, mantenga esta ventana abierta y la sesión se activará automáticamente a
                esa hora.{' '}
              </p>
            </>
          }
        />
      ),
    });
  }

  handleShowDropZone(ev) {
    ev.preventDefault();
    this.setState({ dropZoneShow: true });
  }
  handleFinishCall(ev) {
    ev.preventDefault();
    if (window.confirm("¿Desea desconectarse de la sesión?")) {
      this.otSession.disconnect();
      this.setState({ hangUp: true });
    }
  }

  initSession(sessionData: SessionData) {
    const role = sessionData.type === 'videocall' ? 'publisher' : sessionData.attendant.role;
    this.initOT(
      sessionData.sessionId,
      sessionData.type,
      sessionData.token,
      role,
      sessionData.testSession.sessionId,
      sessionData.testSession.token
    );
  }

  abortConnectOp(title: string, reason: React.Node) {
    this.setState({
      isLoading: false,
      mainScreenMessage: <AlertComponent title={title}>{reason}</AlertComponent>,
    });
  }

  async componentDidMount() {
    const {
      match: {
        params: { sessionId, token },
      },
    } = this.props;

    const supportsScreenSharing = await this.supportsScreenSharing();

    const sessionData: SessionData = await apiPost(`getSessionData`, {
      session: sessionId,
      token,
    });

    const proyectoData: ProyectoData = await apiPost(`getProyectoData`, {
        proyectoID: sessionData.proyectoID
    });

    this.setState(
      st => ({
        ...st,
        supportsScreenSharing,
        sessionData: { ...sessionData, publicDetails: { sessionId, token } },
        proyectoData:{ ...proyectoData },
        isLoading: true,
        clients: sessionData.attendants,
      }),
      () => {
        try {
          const now = new Date().getTime();

          if (now > sessionData.scheduledDatetime + 1000 * sessionData.sessionDuration) {
            const t = new Date(sessionData.scheduledDatetime);
            this.setState({
              isLoading: false,
              mainScreenMessage: (
                <AlertComponent title="La sesión ha finalizado ya.">
                  Estaba programada para esta fecha y hora:
                  <br />
                  {`${moment(t).format('LLLL')}`}
                </AlertComponent>
              ),
            });
            return;
          }
          if (now < sessionData.scheduledDatetime) {
            this.handleWaitForSession(sessionData, proyectoData);
            return;
          }
          this.initSession(sessionData);
        } catch (err) {
          console.error(err);
          alert('error');
        }
      }
    );
  }

  componentWillUnmount() {
    if (this.sessionStartTimeout != null) {
      clearTimeout(this.sessionStartTimeout);
    }
  }

  // @todo: esto dara problemas si el usuario tiene el reloj mal ajustado.
  checkIfSessionTimeReached(sessionData: SessionData, stThreshold: number) {
    const now = new Date().getTime();
    const sessionStartTimeReached = now + stThreshold >= sessionData.scheduledDatetime;
    if (sessionStartTimeReached) {
      this.sessionStartTimeout = null;
      this.setState({ mainScreenMessage: null, isLoading: true }, () => {
        this.initSession(sessionData);
      });
    } else {
      this.addTimeoutToCheckSessionTimeReached(sessionData, stThreshold);
    }
  }

  addTimeoutToCheckSessionTimeReached(sessionData: SessionData, stThreshold: number = 10000) {
    this.sessionStartTimeout = setTimeout(
      () => this.checkIfSessionTimeReached(sessionData, stThreshold),
      stThreshold
    );
  }

  /*
@todo: cambiar nombre a func.
  initPublisher(token: string) {
    if (OT.checkSystemRequirements() != 1) {
      alert('Necesitas usar un navegador que soporte WebRTC.');
      return;
    }

    const publisher = OT.initPublisher(
      'mainOTScreen',
      {
        insertMode: 'append',
        width: '100%',
        height: '100%',
      },
      err => {
        if (err) {
          this.handleError(err);
        }
      }
    );

    publisher.on('streamCreated', event => {
      console.log('*** initPublisher: Stream created.');

      console.log('Subs are: ', this.otSession.getSubscribersForStream(event.stream));
    });

    this.otSession.connect(token, error => {
      if (error) {
        console.log('Error connecting: ', error.name, error.message);
      } else {
        console.log('*** initPublisher: Connected to the session.');
        this.otSession.publish(publisher, err => {
          if (err) {
            this.handleError(err);
            return;
          }
          this.setState({ isLoading: false });
        });
      }
    });
  }
*/
  initSubscriber(token: string) {
    if (OT.checkSystemRequirements() != 1) {
      alert('Necesitas usar un navegador que soporte WebRTC.');
      return;
    }

    this.otSession.on('streamCreated', event => {
      console.log('*** initSubscriber: Stream created.');

      console.log(
        '[streamCreated] Current subscribers are: ',
        this.otSession.getSubscribersForStream(event.stream)
      );

      this.otSession.subscribe(
        event.stream,
        'mainOTScreen',
        {
          insertMode: 'append',
          width: '100%',
          height: '100%',
        },
        err => {
          if (err) {
            this.handleError(err);
          }
        }
      );
    });

    this.otSession.connect(token, error => {
      if (error) {
        console.log('Error connecting: ', error.name, error.message);
      } else {
        console.log('*** initSubscriber: Connected to the session.');
        this.setState({ isLoading: false });
      }
    });
  }

  handleNonRecoverableError() {
    this.setState({
      isLoading: false,
      mainScreenMessage: ErrorComponent({}),
    });
  }

  supportsScreenSharing = () =>
    new Promise((resolve, reject) => {
      try {
        OT.checkScreenSharingCapability(response => {
          resolve((!response.supported || response.extensionRegistered === false) === false);
        });
      } catch (err) {
        reject(err);
      }
    });

  getMyConnectionId = () => this.otSession.connection.connectionId;

  initPublisher = (targetId: string, videoSource: string | null = null) => {
    console.log('inside initp');
    return new Promise((resolve, reject) => {
      const opts = {
        insertMode: 'append',
        width: '15vw',
        height: '15vh',
      };

      Promise.all([
        OT.getUserMedia({
          videoSource: null
        })
      ])
      .then(([micStream]) => {
          if (videoSource) {
            opts.videoSource = videoSource;
          }
          opts.audioSource = micStream.getAudioTracks()[0];
          const target = document.getElementById(targetId);

          const pub = OT.initPublisher(target, opts, err => {
            if (err) {
              console.log('err');
              reject(err);
            } else {
              console.log('resolve');
              resolve(pub);
            }
          });
        });
    });
  };

  async initVideocallPublisher(token: string) {
    if (OT.checkSystemRequirements() != 1) {
      alert('Necesitas usar un navegador que soporte WebRTC.');
      return;
    }
    console.log('starting initVideocallPublisher');
    this.publisher = await this.initPublisher('vcSecondaryScreen');
    console.log('this.publisher: ', this.publisher);
    this.otSession.on('streamCreated', event => {
      console.log('*** initVideocallPublisher: Stream created.');

      console.log(', subs are: ', this.otSession.getSubscribersForStream(event.stream));

      this.otSession.subscribe(
        event.stream,
        'mainOTScreen',
        {
          insertMode: 'append',
          width: '100%',
          height: '100%',
        },
        err => {
          if (err) {
            this.handleError(err);
          }
        }
      );
    });

    this.publisher.on('disconnected', event => {
      console.log('PUBLISHER DISCONNECTED', event);
    });

    this.publisher.on('streamDestroyed', event => {
      console.log('PUBLISHER streamDestroyed', event);
    });

    this.publisher.on('streamCreated', event => {
      event.preventDefault();
      console.log('*** initVideocallPublisher: Stream destroyed.');
    });

    this.otSession.connect(token, error => {
      if (error) {
        console.log('Error connecting: ', error.name, error.message);
      } else {
        console.log('*** initVideocallPublisher: Connected to the session.');

        const activeConnectionsForCurrentPub = this.otSession.connections
          .where({ data: this.otSession.connection.data })
          .filter(c => c.id !== this.otSession.connection.id).length;

        if (activeConnectionsForCurrentPub > 0) {
          this.abortConnectOp(
            '¡Ya está conectado a esta sesión desde algún navegador!',
            'No puede conectarse varias veces a la misma sesión. Si piensa que es un error, por favor, refresque la página y en unos minutos reinténtelo de nuevo.'
          );

          return;
        }

        this.otSession.publish(this.publisher, err => {
          if (err) {
            console.log('*** initVideocallPublisher: Publish ERROR.', err);
            this.handleError(err);
            return;
          }
          console.log('*** initVideocallPublisher: Started to publish.');
          this.setState({ isLoading: false, lastSessionDurationDeltaSent: new Date().getTime() });
          if (this.sessionDurationTrackingInterval != null) {
            clearInterval(this.sessionDurationTrackingInterval);
          }
          this.sessionDurationTrackingInterval = setInterval(() => {
            console.log('tracking interval: ', this.state.lastSessionDurationDeltaSent);

            apiPost('updateSessionDuration', {
              session: this.state.sessionData.publicDetails.sessionId,
              token: this.state.sessionData.publicDetails.token,
              delta: new Date().getTime() - this.state.lastSessionDurationDeltaSent,
            }).then(() => {
              this.setState(st => ({
                ...st,
                lastSessionDurationDeltaSent: new Date().getTime(),
              }));
            });
          }, 15000);
        });
      }
    });
  }

  async handleEnableScreenShare() {
    this.setState({ screenSharingEnabled: true }, async () => {
      this.otSession.unpublish(this.publisher);

      this.publisher = await this.initPublisher('vcSecondaryScreen', 'screen');

      this.otSession.publish(this.publisher, err => {
        if (err) {
          console.log('*** handleEnableScreenShare: Publish ERROR.', err);
          this.handleError(err);
          return;
        }
        console.log('*** handleEnableScreenShare: Started to publish.');
      });
    });
  }

  async handleDisableScreenShare() {
    this.setState({ screenSharingEnabled: false }, async () => {
      this.otSession.unpublish(this.publisher);

      this.publisher = await this.initPublisher('vcSecondaryScreen');

      this.otSession.publish(this.publisher, err => {
        if (err) {
          console.log('*** handleDisableScreenShare: Publish ERROR.', err);
          this.handleError(err);
          return;
        }
        console.log('*** handleDisableScreenShare: Started to publish.');
      });
    });
  }

  handleStatusChange(connectionData: string, newStatus: number) {
    const userData = QueryString.parse(connectionData);
    this.setState(st => {
      const clients = st.clients.reduce((obj, client) => {
        if (client.data.idx === userData.idx) {
          const newClient = { ...client };
          newClient.isConnected = newStatus;
          return [...obj, newClient];
        }
        return [...obj, client];
      }, []);
      return { ...st, clients };
    });
  }

  async handleSessionSignal(kind: string, event: any) {
    const userDetails = QueryString.parse(event.from.data);
    let eventData = null;
    if (kind === 'msg') {
      eventData = await this.asyncJSONparse(event.data);
    } else {
      eventData = await this.asyncJSONparse(event.data);
    }

    const obj = {
      userDetails,
      kind,
      eventData,
      showTime: moment().format('HH:mm'), // :ss'),
      source:
        event.from.connectionId === this.otSession.connection.connectionId ? 'mine' : 'theirs',
      inProp: false,
    };

    if (kind === 'msg') {
      obj.sent = eventData.sent || false;
    }

    this.setState(
      st => ({
        ...st,
        chats: [...st.chats, obj],
      }),
      () => {
        setTimeout(() => {
          console.log('inside settimeout');
          this.setState(
            st => ({
              ...st,
              chats: st.chats.map(c =>
                'lid' in c.eventData && c.eventData.lid === obj.eventData.lid
                  ? { ...c, inProp: true }
                  : c
              ),
            }),
            () => {
              document.getElementById(`chatMsg${this.state.chats.length - 1}`).scrollIntoView();
            }
          );
        }, 1);
      }
    );
  }

  bindSessionListeners(session: Session) {
    session.on('unsubscribe', event => {
      console.log('otSession unsubscribe');
      console.log(
        'on stream destroyed (pub), subs are: ',
        session.getSubscribersForStream(event.stream)
      );
    });

    session.on('connectionCreated', event => {
      this.handleStatusChange(event.connection.data, true);
      console.log('*** bindSessionListeners/Connection Created   ', event);
      event.target.streams.forEach(s => {
        console.log('Existing stream: ', s);
      });
    });

    session.on('connectionDestroyed', event => {
      this.handleStatusChange(event.connection.data, false);
      console.log('on connectionDestroyed', event);
    });

    session.on('streamDestroyed', event => {
      console.log(`Stream stopped. Reason: ${event.reason}`, event);
      const subscribers = this.otSession.getSubscribersForStream(event.stream);
      console.log('subs are: ', subscribers);
    });
  }

  sendSignal = (session, signal) =>
    new Promise((resolve, reject) => {
      session.signal(signal, err => {
        if (err) {
          reject(err);
        } else {
          resolve(true);
        }
      });
    });

  async sendChatSignal(sess: {}, val: any) {
    const serVal = await this.asyncJSONstringify(val);

    return this.sendSignal(sess, {
      type: 'msg',
      data: serVal,
    });
  }

  asyncJSONstringify = (obj: any) =>
    new Promise((resolve, reject) => {
      try {
        const result = JSON.stringify(obj);
        resolve(result);
      } catch (err) {
        reject(err);
      }
    });

  asyncJSONparse = (str: string) =>
    new Promise((resolve, reject) => {
      try {
        const result = JSON.parse(str);
        resolve(result);
      } catch (err) {
        reject(err);
      }
    });

  async sendChatFileSignal(sess, obj) {
    const serVal = await this.asyncJSONstringify(obj);

    await this.sendSignal(sess, {
      type: 'file',
      data: serVal,
    });
    return true;
  }

  bindChatListeners(form, session: string) {
    form.addEventListener('submit', async event => {
      event.preventDefault();

      const msgTxt = this.chatInputRef.current;

      if (msgTxt.value.trim() === '') {
        return;
      }

      const lidgen = getNewId();
      const lid = lidgen.next().value;
      await this.sendChatSignal(this.otSession, { msg: msgTxt.value, lid, sent: false });

      apiPost('saveChatHistory', {
        session: this.state.sessionData.publicDetails.sessionId,
        token: this.state.sessionData.publicDetails.token,
        messages: [{ message: msgTxt.value, ts: new Date().getTime() }],
      }).then(() => {
        this.setState(st => ({
          ...st,
          chats: st.chats.map(c => (c.eventData.lid === lid ? { ...c, sent: true } : c)),
        }));
      });

      msgTxt.value = '';
    });

    session.on('signal:msg', e => this.handleSessionSignal('msg', e));
    session.on('signal:file', e => this.handleSessionSignal('file', e));
  }

  async networkTest(sessionId: string, token: string) {
    const otNetworkTest = new NetworkTest(OT, {
      apiKey: OPENTOK_API_KEY,
      sessionId,
      token,
    });

    const connectivityResults = await otNetworkTest.testConnectivity();
    const intNetworkStats = stats => {
      console.log('intermediate testQuality stats', stats);
    };
    const qualityResults = await otNetworkTest.testQuality(intNetworkStats);

    console.log('OpenTok quality results', qualityResults);
    const publisherSettings = {};
    if (qualityResults.video.reason) {
      console.log('Video not supported:', qualityResults.video.reason);
      publisherSettings.videoSource = null; // audio-only
    } else {
      publisherSettings.frameRate = qualityResults.video.recommendedFrameRate;
      publisherSettings.resolution = qualityResults.video.recommendedResolution;
    }
    if (!qualityResults.audio.supported) {
      console.log('Audio not supported:', qualityResults.audio.reason);
      publisherSettings.audioSource = null;
      // video-only, but you probably don't want this -- notify the user?
    }
    if (!publisherSettings.videoSource && !publisherSettings.audioSource) {
      // Do not publish. Notify the user.
    } else {
      // Publish to the "real" session, using the publisherSettings object.
    }

    console.log('RESULT: ', publisherSettings);
  }

  async initOT(sessionId, sessionType, token, role, testSessionId, testToken) {
    console.log('initOT: ', [testSessionId, testToken]);

    if (DISABLE_NETWORK_TEST === false) {
      await this.networkTest(testSessionId, testToken);
      console.log('start sess');
    }
    this.otSession = await OT.initSession(OPENTOK_API_KEY, sessionId);

    this.bindSessionListeners(this.otSession);
    this.bindChatListeners(this.chatFormRef.current, this.otSession);

    if (sessionType === 'videocall') {
      await this.initVideocallPublisher(token);
    } else if (sessionType === 'webinar') {
      if (role === 'publisher') {
        // this.initPublisher(token);
      } else {
        this.initSubscriber(token);
      }
    }
  }

  handleError(err) {
    console.log('ERROR!');
    console.log(err);
  }

  didSessionMembersConnect = (clients: Array<{ isConnected: boolean }>) => {
    return clients.map(c => c.isConnected === true).filter(x => x === true).length > 1;
  };

  filesAddedToChatHandler(files: {}, results: [{}]): Array<any> {
    return results.map((r: {}, idx: number) =>
      this.sendChatFileSignal(this.otSession, { ...files[idx], result: r })
    );
  }

  chatMessagesRender = (chats: Array<{ kind: 'msg' | 'file' }>): Array<React.Node> => {
    return chats.map((c, chatId) => {
      if (c.kind === 'msg') {
        return <ChatMessage message={c} chatId={chatId} inProp={c.inProp} />;
      }

      return <ChatAttachment message={c} chatId={chatId} />;
    });
  };

  render() {
    const buttons = (
      <div
        style={{ textAlign: 'center', alignContent: 'center', alignItems: 'center', marginTop: 10 }}
      >
        {this.state.supportsScreenSharing &&
          (this.state.screenSharingEnabled === false ? (
            <button
              onClick={ev => {
                ev.preventDefault();
                this.handleEnableScreenShare();
              }}
              type="button"
              className="btn btn-secondary btn-sm btn-block"
            >
              Compartir pantalla
            </button>
          ) : (
            <button
              onClick={ev => {
                ev.preventDefault();
                this.handleDisableScreenShare();
              }}
              type="button"
              className="btn btn-secondary btn-sm btn-block active"
            >
              Dejar de compartir pantalla
            </button>
          ))}
        <button
          onClick={this.handleShowDropZone}
          type="button"
          className="btn btn-secondary btn-sm btn-block"
        >
          Compartir fichero
        </button>
      </div>
    );
    const buttonFinishCall = (
      <div
        style={{ 
          textAlign: 'center',
          alignContent: 'center',
          alignItems: 'center',
          marginTop: 10,
          display:
              this.state.hangUp === true ? 'none' : 'block',
        }}
      >
        <button
          onClick={this.handleFinishCall}
          type="button"
          className="btn btn-danger btn-sm btn-block"
        >
          Finalizar llamada
        </button>
      </div>
    );

    return (
      <div>
        <header className="App-header">
          <img src={logo} style={{ height: '70px', padding: '10px', float: 'left' }} />
          <h3
            style={{
              display: 'block',
              float: 'left',
              marginLeft: '10px',
              fontSize: '1.4em',
              letterSpacing: '0.035em',
              fontWeight: 800,
            }}
          >
            Sesión de videollamada online.
          </h3>
        </header>

        <div
          id="loading"
          style={{ padding: '20px', display: this.state.isLoading === true ? 'block' : 'none' }}
        >
          <span style={{ marginBottom: '30px', marginTop: '10vh', display: 'block' }}>
            <h2 style={{ color: '#333' }}>Cargando sesión.</h2>
            <h5 style={{ textTransform: 'uppercase', color: '#999' }}>
              por favor, espere unos instantes...
            </h5>
          </span>
          <div
            className="progress"
            style={{ marginLeft: 'auto', marginRight: 'auto', width: '50%', height: '2rem' }}
          >
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              aria-valuenow="100"
              aria-valuemin="0"
              aria-valuemax="100"
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div
          id="wrapper"
          style={{
            width: '100vw',
            overflow: 'hidden',
            display:
              this.state.isLoading === true || this.state.mainScreenMessage !== null || this.state.hangUp === true 
                ? 'none'
                : 'block',
          }}
        >
          <div
            id="mainOTWrap"
            class="card"
            style={{
              width: '70%',
              overflow: 'hidden',
              float: 'left',
              margin: '20px',
              height: 'calc(100vh - 130px)',
              width: 'calc(70vw - 40px)',
            }}
          >
            <div id="mainOTScreen" style={{ height: '100%' }}>
              <div
                id="mainOTScreenPH"
                style={
                  this.didSessionMembersConnect(this.state.clients)
                    ? { overflow: 'hidden', display: 'none' }
                    : { overflow: 'hidden', display: 'block' }
                }
              >
                <WaitingForSession
                  title="El otro usuario no está conectado."
                  subheading="Por favor, permanezca a la espera."
                />
              </div>
            </div>
            <div
              id="vcSecondaryScreen"
              style={{
                position: 'absolute',
                left: 'calc(50vw - 40px)',
                top: 'calc(80vh - 130px)',
                border: '1px solid #fff'
              }}
            />
          </div>
          <div
            id="rightwrap"
            style={{
              width: '30%',
              height: '88vh',
              overflow: 'hidden',
              float: 'left',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                height: '60%',
                overflow: 'hidden',
                paddingTop: 25,
                paddingRight: 10,
              }}
            >
              <div className="card" style={{ height: '100%', marginRight: '1vw', padding: '1vw' }}>
                <h5
                  className="card-header"
                  style={{
                    fontSize: '0.9em',
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    textAlign: 'center',
                  }}
                >
                  Chat
                </h5>
                <div
                  className="card-body"
                  style={{
                    minHeight: '25%',
                    margin: '3px',
                    marginTop: '2px',
                    marginBottom: '3px',
                    marginLeft: 0,
                    overflow: 'hidden',
                    overflowY: 'scroll',
                  }}
                >
                  <div
                    id="history"
                    style={{
                      textAlign: 'left',
                      fontSize: '0.85em',
                    }}
                  >
                    {this.state.dropZoneShow ? (
                      <DropZone
                        onDrop={async acceptedFiles => {
                          const getFilePromise = file =>
                            apiPost(
                              'uploadSessionFile',
                              {
                                session: this.state.sessionData.publicDetails.sessionId,
                                token: this.state.sessionData.publicDetails.token,
                                file,
                              },
                              false
                            );

                          const results = await Promise.all(acceptedFiles.map(getFilePromise));
                          this.setState({ dropZoneShow: false }, () => {
                            this.filesAddedToChatHandler(acceptedFiles, results);
                          });
                        }}
                        handleCloseUploadOverlay={() => {
                          this.setState({ dropZoneShow: false });
                        }}
                      />
                    ) : (
                      this.chatMessagesRender(this.state.chats)
                    )}
                  </div>
                </div>
                <form
                  ref={this.chatFormRef}
                  style={{ marginLeft: 'auto', marginTop: 3, marginRight: '7%', width: '90%' }}
                >
                  <input
                    className="form-control"
                    ref={this.chatInputRef}
                    type="text"
                    autoComplete="off"
                    placeholder="Intruduzca texto para escribir"
                    id="msgTxt"
                  />
                </form>
                {buttons}
                {buttonFinishCall}
              </div>
            </div>
            <div
              id="attendants"
              style={{
                width: '98%',
                height: '25vh',
                textAlign: 'left',
                paddingRight: 10,
                overflow: 'hidden',
                margin: 10,
                marginTop: 20,
                marginLeft: 0,
              }}
            >
              <div className="card" style={{ height: '100%', marginRight: '1vw', padding: '1vw' }}>
                <h5
                  className="card-header"
                  style={{
                    fontSize: '0.9em',
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    textAlign: 'center',
                  }}
                >
                  Asistentes
                </h5>
                <div
                  className="card-body"
                  style={{
                    minHeight: '80%',
                    margin: '3px',
                    marginTop: '2px',
                    marginBottom: '3px',
                    marginLeft: 0,
                    overflow: 'hidden',
                    overflowY: 'scroll',
                  }}
                >
                  {this.state.clients.map(client => {
                    const isConnected = 'isConnected' in client && client.isConnected === true;

                    return (
                      <div
                        key={client.data.idx}
                        style={{
                          display: 'block',
                          textAlign: 'left',
                          width: '100%',
                          padding: '4px',
                        }}
                      >
                        <div style={{ display: 'inline-grid' }}>
                          <div style={{ marginTop: '5px', display: 'inline' }}>
                            <img src={userAvatar1} style={{ height: '25px' }} />
                            <span
                              style={{
                                position: 'absolute',
                                height: '12px',
                                width: '12px',
                                backgroundColor: isConnected === true ? '#00ff00' : '#ff0000',
                                borderRadius: '50%',
                                display: 'inline-block',
                                marginLeft: '-8px',
                                marginTop: '-8px',
                              }}
                            />
                          </div>
                        </div>
                        <span style={{ paddingLeft: '14px', color: '#666', fontSize: '11pt' }}>
                          {reprClientName(client.data)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        {this.state.mainScreenMessage && (
          <div id="mainScreenMessage">{this.state.mainScreenMessage}</div>
        )}
      </div>
    );
  }
}

export default SessionComponent;
